<template>
    <div class="relative flex flex-col h-screen" @touchstart="resetInactivityTimer" @keydown="resetInactivityTimer">
        <img :src="getImage('mascot')" class="fixed papsi-mascot">

        <div class="flex flex-1 lg:flex-row flex-col">
            <SidebarSection>
                <LabelControl type="message" :content="$t('enp001')" />
            </SidebarSection>

            <ContentSection :show_logout="false" :show_back="true">
                <div>
                    <div class="flex flex-col">
                        <LabelControl type="heading" :content="$t('enp002')" />
                        <div v-if="this.login_type === 'account' || this.login_type === 'serial'" class="flex flex-row mt-3 text-grey-dark uppercase text-2xl font-medium">
                            <p>{{ this.login_type }} number:</p>
                            <p class="ml-3">{{ this.account_serial_number }}</p>
                        </div>
                    </div>
                    <KeypadControl input_type="PIN" @digits-full="handlePin" />
                </div>
            </ContentSection>
        </div>

        <FooterSection />
    </div>
</template>

<script>
import SidebarSection from '@/components/sections/SidebarSection.vue';
import ContentSection from '@/components/sections/ContentSection.vue';
import FooterSection from '@/components/sections/FooterSection.vue';
import LabelControl from '@/components/controls/LabelControl.vue';
import KeypadControl from '@/components/controls/KeypadControl.vue';
import mixin from '@/mixins/controls.js';

import user from '@/services/user';

export default {
    mixins: [mixin],
    components: {
        SidebarSection,
        ContentSection,
        FooterSection,
        LabelControl,
        KeypadControl
    },
    inject: ['setLoading', 'setError'],
    data() {
        return {
            login_type: this.$store.state.login_params.type || 'serial',
            account_serial_number: this.$store.state.login_params.value || '',
            inactivity_timeout: null,
        };
    },
    mounted() {
        this.$emit('checkauth', false);
        this.startInactivityTimer();
        if(!this.account_serial_number){
            this.$router.back();
        }
        window.addEventListener("touchstart", this.resetInactivityTimer);
    },
    beforeUnmount() {
        clearTimeout(this.inactivity_timeout);
        window.removeEventListener("touchstart", this.resetInactivityTimer);
    },
    methods: {
        resetInactivityTimer() {
            clearTimeout(this.inactivity_timeout);
            this.startInactivityTimer();
        },
        startInactivityTimer() {
            if (process.env.VUE_APP_DEVELOPMENT_MODE === 'false') {
                this.inactivity_timeout = setTimeout(() => {
                this.$router.push({ name: "start-page" });
                }, process.env.VUE_APP_IDLE_DELAY || 30000);
            } 
        },
        handlePin(pin) {
            const pinHandlerMap = {
                qr: this.handleQRPin,
                account: this.handleAccountPin,
                serial: this.handleSerialPin,
            };

            const handler = pinHandlerMap[this.login_type];
            if (handler) {
                handler(pin);
            } else {
                this.showError(`Unknown login type ${this.login_type}`);
            }
        },
        handleQRPin(pin) {
            let pin_string = pin.join('');
            this.$store.state.user.old_pin = pin_string;
            const { hash, value: qr, uuid } = this.$store.state.login_params;

            if (!pin || !hash || !qr) {
                this.showError('Incomplete login information');
                return;
            }
            this.setLoading(true);
            user.validatePin(pin_string, hash, 2, uuid, qr)
                .then(() => {
                    this.loginQR(hash, qr);
                })
                .catch((error) => {
                    this.showError(error.response.data.message);
                    this.setLoading(false);
                });
        },
        loginQR(hash, qr) {
            user.createSessionQR(hash, qr, 2).then((response) => {
                this.saveUserData(response.data.data);
                // this.setLoading(false);
                // this.$router.push({ name: 'services-page' });
                // this.showSuccess('Login successful!');
                if(this.$store.state.user && (this.$store.state.user.pso_enroll || this.$store.state.user.pin_update)){
                    this.$router.push({ name: 'edit-pin-page' });
                    this.setLoading(false);
                    this.showSuccess('Login successful!');
                    return;
                } else {
                    this.$router.push({ name: 'services-page' });
                    this.setLoading(false);
                    this.showSuccess('Login successful!');
                }
            }).catch((error) => {
                this.showError(error.response.data.message);
                this.setLoading(false);
            });
        },
        handleAccountPin(pin) {
            let pin_string = pin.join('');
            this.$store.state.user.old_pin = pin_string;
            const { hash, value, uuid } = this.$store.state.login_params;

            if (!pin || !hash || !value) {
                this.showError('Incomplete login information');
                return;
            }
            this.setLoading(true);
            user.validatePin(pin_string, hash, 3, uuid, value)
                .then(() => { this.loginAccount(hash, value); })
                .catch((error) => {
                    this.showError(error.response.data.message);
                    this.setLoading(false);
                });
        },
        loginAccount(hash, value) {
            user.createSessionAccount(hash, value, 3).then((response) => {
                this.saveUserData(response.data.data);
                if(this.$store.state.user && (this.$store.state.user.pso_enroll || this.$store.state.user.pin_update)){
                    this.$router.push({ name: 'edit-pin-page' });
                    this.setLoading(false);
                    this.showSuccess('Login successful!');
                    return;
                } else {
                    this.$router.push({ name: 'services-page' });
                    this.setLoading(false);
                    this.showSuccess('Login successful!');
                }
            }).catch((error) => {
                this.showError(error.response.data.message);
                this.setLoading(false);
            });
        },
        handleSerialPin(pin) {
            let pin_string = pin.join('');
            this.$store.state.user.old_pin = pin_string;
            const { hash, value, uuid } = this.$store.state.login_params;
            if (!pin || !hash || !value) {
                this.showError('Incomplete login information');
                return;
            }
            this.setLoading(true);
            user.validatePin(pin_string, hash, 4, uuid, value)
                .then(() => {
                    this.loginSerial(hash, value);
                })
                .catch((error) => {
                    this.showError(error.response.data.message);
                    this.setLoading(false);
                });
        },
        loginSerial(hash, value) {
            user.createSessionSerial(hash, value, 4).then((response) => {
                this.saveUserData(response.data.data);
                // this.setLoading(false);
                // this.$router.push({ name: 'services-page' });
                // this.showSuccess('Login successful!');
                if(this.$store.state.user && (this.$store.state.user.pso_enroll || this.$store.state.user.pin_update)){
                    this.$router.push({ name: 'edit-pin-page' });
                    this.setLoading(false);
                    this.showSuccess('Login successful!');
                    return;
                } else {
                    this.$router.push({ name: 'services-page' });
                    this.setLoading(false);
                    this.showSuccess('Login successful!');
                }
            }).catch((error) => {
                this.showError(error.response.data.message);
                this.setLoading(false);
            });
        },
    }

};
</script>

<style scoped>
.papsi-mascot {
    bottom: 0;
    left: 0;
    width: 25rem;
}

.papsi-container {
    height: 47rem;
}

.papsi-text {
    height: 36rem;
}
</style>
